import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { Toolbar } from '../components/toolbar/toolbar.tsx';
import { ToolbarTitle } from '../components/toolbar/toolbar-title.tsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout } from '../components/layout/layout.tsx';
import { ToolbarSeparator } from '../components/toolbar/toolbar-separator.tsx';
import { LoaderRender } from '../components/render/loader-render.tsx';
import { ErrorRender } from '../components/render/error-render.tsx';
import type { SamplerUser, SamplingJobMetric } from '../services/api.types.ts';
import { getSamplers, getSamplingJobMetrics } from '../services/api.ts';
import { SamplerDialog } from '../dialog/sampler-dialog.tsx';
import { MetricsSamplersDrawer } from '../content/layout/metrics-samplers-drawer.tsx';
import { DropDownList, type DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { SamplingMetricsChart } from '../content/chart/sampling-metrics-chart.tsx';
import { SamplingJobChartType } from '../content/chart/sampling-metrics-chart.types.ts';
import { DatePicker, type DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { NoDataRender } from '../components/render/no-data-render.tsx';
import { getTargetMonth } from '../utils/date.ts';
import { Button } from '@progress/kendo-react-buttons';
import { MetricsSummaryDrawer } from '../content/layout/metrics-summary-drawer.tsx';
import { LayoutDrawerPosition } from '../components/layout/layout-drawer.types.ts';
import styles from './sampling-metrics-page.module.scss';

const BREADCRUMB_ITEMS = [
  {
    id: 'homepage',
    text: 'Home',
    url: '/',
    icon: <FontAwesomeIcon icon={faHome} size={'lg'} />,
  },
  {
    id: 'sampling-metrics',
    text: 'Sampling Metrics',
    url: '/sampling-metrics',
  },
];

const CHART_TYPES = [
  {
    id: SamplingJobChartType.Sampler,
    title: 'Sampler',
  },
  {
    id: SamplingJobChartType.JobStatus,
    title: 'Job Status',
  },
  {
    id: SamplingJobChartType.SampleType,
    title: 'Sample Type',
  },
  {
    id: SamplingJobChartType.SamplingStatus,
    title: 'Sampling Status',
  },
];
const CURRENT_SEASON_START = getTargetMonth(5);

const getOrderedSamplers = async () => {
  const samplers = await getSamplers({ showInactive: false });
  return samplers.sort((a, b) => {
    const forenameCompare = a.forename.localeCompare(b.forename);
    if (forenameCompare !== 0) {
      return forenameCompare;
    }

    return a.surname.localeCompare(b.surname);
  });
};

export function SamplingMetricsPage() {
  const [chartType, setChartType] = useState<SamplingJobChartType>(SamplingJobChartType.Sampler);
  const [samplerUsers, setSamplerUsers] = useState<SamplerUser[]>([]);
  const [openSamplerUser, setOpenSamplerUser] = useState<SamplerUser | null>(null);
  const [jobMetrics, setJobMetrics] = useState<SamplingJobMetric[]>([]);
  const [fromFilter, setFromFilter] = useState<Date | undefined>(CURRENT_SEASON_START);
  const [toFilter, setToFilter] = useState<Date | undefined>(undefined);
  const [samplerDrawerOpen, setSamplerDrawerOpen] = useState(true);
  const [summaryDrawerOpen, setSummaryDrawerOpen] = useState(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const samplers = await getOrderedSamplers();
        if (isMounted) {
          setSamplerUsers(samplers);
        }
      } catch (error) {
        console.error('Error loading sampling job metrics:', error);
        if (isMounted) {
          setError('Unable to load sampling job metrics');
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      setLoading(true);
      try {
        const metrics = await getSamplingJobMetrics({ startDate: fromFilter, endDate: toFilter });
        if (isMounted) {
          setJobMetrics(metrics);
        }
      } catch (error) {
        console.error('Error loading sampling job metrics:', error);
        if (isMounted) {
          setError('Unable to load sampling job metrics');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [fromFilter, toFilter]);

  const samplingCapacity = useMemo(
    () =>
      samplerUsers.reduce((acc: number, item: SamplerUser) => acc + item.weeklyWorkdays * item.weeklySampleEstimate, 0),
    [samplerUsers]
  );

  const renderSamplersDrawer = useCallback(
    (position: LayoutDrawerPosition) => {
      return (
        <MetricsSamplersDrawer
          data={samplerUsers}
          position={position}
          width={'15%'}
          maxWidth={350}
          open={samplerDrawerOpen}
          onOpen={() => setSamplerDrawerOpen((prev) => !prev)}
          onOpenDialog={({ sampler }) => setOpenSamplerUser(sampler)}
        />
      );
    },
    [samplerDrawerOpen, samplerUsers]
  );

  const renderSummaryDrawer = useCallback(
    (position: LayoutDrawerPosition) => {
      return (
        <MetricsSummaryDrawer
          data={jobMetrics}
          chartType={chartType}
          samplingCapacity={samplingCapacity}
          position={position}
          width={'20%'}
          maxWidth={450}
          open={summaryDrawerOpen}
          onOpen={() => setSummaryDrawerOpen((prev) => !prev)}
        />
      );
    },
    [jobMetrics, chartType, samplingCapacity, summaryDrawerOpen]
  );

  const handleDateFilterChange = ({ target }: DatePickerChangeEvent) => {
    const { name, value } = target;
    if (name === 'fromDate') {
      setFromFilter(value ?? undefined);
    } else {
      setToFilter(value ?? undefined);
    }
  };

  const handleTypeChange = ({ value }: DropDownListChangeEvent) => {
    setChartType(value.id);
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const metrics = await getSamplingJobMetrics({ startDate: fromFilter, endDate: toFilter });
      setJobMetrics(metrics);
    } catch (error) {
      console.error('Error loading sampling job metrics:', error);
      setError('Unable to load sampling job metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleDialogApply = async () => {
    try {
      const samplers = await getOrderedSamplers();
      setSamplerUsers(samplers);
    } catch (error) {
      console.error('Error loading samplers:', error);
      setError('Unable to load samplers');
    } finally {
      handleDialogClose();
    }
  };

  const handleDialogClose = () => {
    setOpenSamplerUser(null);
  };

  return (
    <>
      <Layout
        breadcrumb={BREADCRUMB_ITEMS}
        renderDrawer={{
          left: renderSamplersDrawer,
          right: renderSummaryDrawer,
        }}
      >
        <Toolbar>
          <ToolbarTitle flex={1}>Sampling Metrics</ToolbarTitle>
          <ToolbarSeparator />
          <div style={{ width: 200, marginRight: 5 }}>
            <DatePicker
              name={'fromDate'}
              value={fromFilter}
              max={toFilter}
              format={'EEE, d MMM yy'}
              placeholder={'Filter jobs planned after..'}
              onChange={handleDateFilterChange}
            />
          </div>
          <div style={{ width: 200 }}>
            <DatePicker
              name={'toDate'}
              value={toFilter}
              min={fromFilter}
              format={'EEE, d MMM yy'}
              placeholder={'Filter jobs planned before..'}
              onChange={handleDateFilterChange}
            />
          </div>
          <ToolbarSeparator />
          <DropDownList
            data={CHART_TYPES}
            dataItemKey={'id'}
            textField={'title'}
            value={CHART_TYPES.find((type) => type.id === chartType)}
            onChange={handleTypeChange}
            style={{ width: 200 }}
          />
          <ToolbarSeparator />
          <Button themeColor={'primary'} className={styles.refreshBtn} onClick={handleRefresh}>
            <FontAwesomeIcon icon={faRefresh} size={'1x'} />
          </Button>
        </Toolbar>
        {loading || error ? (
          <div className={styles.contentRender}>
            {loading ? <LoaderRender message={'Loading...'} /> : <ErrorRender message={error} />}
          </div>
        ) : jobMetrics.length === 0 ? (
          <div className={styles.contentChart}>
            <NoDataRender message={'No sampling data found, please ensure filters are correctly set.'} />
          </div>
        ) : (
          <SamplingMetricsChart
            className={styles.contentChart}
            chartType={chartType}
            interactive={true}
            data={jobMetrics}
          />
        )}
      </Layout>
      {openSamplerUser && (
        <SamplerDialog
          sampler={openSamplerUser}
          onApply={handleDialogApply}
          onClose={handleDialogClose}
        ></SamplerDialog>
      )}
    </>
  );
}
